import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Reveal, RevealLines } from './Reveal';
import { CONTACT } from '../data/site';
import { ArrowRight, Phone, Mail, Instagram, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVT_TYPES = ['Mariage', 'Anniversaire', 'Entreprise', 'Soirée privée', 'Inauguration', 'EVG / EVJF', 'Sur mesure'];

export default function Contact() {
  const [form, setForm] = useState({
    nom: '', telephone: '', email: '',
    date_evenement: '', lieu: '', nb_personnes: '',
    type_evenement: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.email) {
      toast.error('Merci de renseigner nom, téléphone et email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success('Demande envoyée. Nous revenons vers vous rapidement.');
      setForm({ nom: '', telephone: '', email: '', date_evenement: '', lieu: '', nb_personnes: '', type_evenement: '', message: '' });
    } catch (err) {
      toast.error('Une erreur est survenue. Merci de réessayer ou de nous appeler.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 md:py-48" data-testid="contact-section">
      <div className="px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
          <Reveal className="col-span-12 md:col-span-3">
            <div className="flex md:flex-col items-baseline md:items-start gap-4">
              <span className="font-mono text-gold text-sm tracking-[0.3em]">08 —</span>
              <span className="overline">Contact</span>
            </div>
          </Reveal>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 6vw, 6rem)' }}>
              <RevealLines lines={['Un événement.', <span key="i" className="italic text-char/50">Une conversation.</span>]} />
            </h2>
            <p className="mt-6 text-char/60 max-w-xl">
              Racontez-nous votre projet — nous vous répondons sous 24h avec un devis personnalisé.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Form */}
          <Reveal className="col-span-12 lg:col-span-8" delay={0.15}>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2" data-testid="quote-form">
              <input aria-label="Nom" className="field md:col-span-1" placeholder="Nom & prénom" value={form.nom} onChange={set('nom')} data-testid="input-nom" />
              <input aria-label="Téléphone" className="field md:col-span-1" placeholder="Téléphone" value={form.telephone} onChange={set('telephone')} data-testid="input-tel" />
              <input aria-label="Email" type="email" className="field md:col-span-2" placeholder="Email" value={form.email} onChange={set('email')} data-testid="input-email" />
              <input aria-label="Date de l'événement" className="field md:col-span-1" placeholder="Date de l'événement" value={form.date_evenement} onChange={set('date_evenement')} onFocus={(e) => (e.target.type = 'date')} onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }} data-testid="input-date" />
              <input aria-label="Lieu" className="field md:col-span-1" placeholder="Lieu (ville, domaine)" value={form.lieu} onChange={set('lieu')} data-testid="input-lieu" />
              <input aria-label="Nombre de personnes" className="field md:col-span-1" placeholder="Nombre de personnes" value={form.nb_personnes} onChange={set('nb_personnes')} data-testid="input-nb" />
              <div className="md:col-span-1 py-2">
                <label className="overline block mb-2">Type d'événement</label>
                <div className="flex flex-wrap gap-2">
                  {EVT_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setForm((f) => ({ ...f, type_evenement: t }))}
                      className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                        form.type_evenement === t
                          ? 'bg-gold text-cream border-gold'
                          : 'border-char/25 text-char/70 hover:border-gold hover:text-gold'
                      }`}
                      data-testid={`type-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <textarea aria-label="Message" rows={4} className="field md:col-span-2 resize-none" placeholder="Décrivez votre projet…" value={form.message} onChange={set('message')} data-testid="input-message" />

              <div className="md:col-span-2 mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 bg-gold text-cream px-9 py-4 text-[12px] tracking-[0.2em] uppercase hover:bg-char transition-colors duration-500 disabled:opacity-60"
                  data-testid="submit-quote"
                >
                  {submitting ? 'Envoi…' : 'Recevoir mon devis'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <span className="text-char/50 text-sm">Réponse sous 24h — devis gratuit.</span>
              </div>
            </form>
          </Reveal>

          {/* Side info */}
          <Reveal className="col-span-12 lg:col-span-4" delay={0.25}>
            <div className="space-y-6 lg:pl-8 lg:border-l lg:border-char/10">
              <ContactRow icon={<Phone size={16} />} label="Téléphone" value={CONTACT.phone} href={`tel:${CONTACT.phoneIntl}`} testid="contact-phone" />
              <ContactRow icon={<Mail size={16} />} label="Email" value={CONTACT.email} href={`mailto:${CONTACT.email}`} testid="contact-email" />
              <ContactRow icon={<Instagram size={16} />} label="Instagram" value="@ofildesverres" href={CONTACT.instagram} testid="contact-ig" />
              <ContactRow icon={<MapPin size={16} />} label="Zone" value="Montpellier · Occitanie · Sud de la France" testid="contact-zone" />

              <div className="mt-8 aspect-[4/3] overflow-hidden border border-char/10">
                <iframe
                  title="Localisation Montpellier"
                  src="https://www.google.com/maps?q=Montpellier,France&z=10&output=embed"
                  className="w-full h-full grayscale contrast-105"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value, href, testid }) {
  const inner = (
    <div className="group flex items-center gap-4 py-3 border-b border-char/10 hover:border-gold/40 transition-colors">
      <span className="w-9 h-9 border border-char/15 flex items-center justify-center text-gold group-hover:border-gold transition-colors">
        {icon}
      </span>
      <div>
        <div className="overline">{label}</div>
        <div className="text-char group-hover:text-gold transition-colors">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" data-testid={testid}>{inner}</a>;
  return <div data-testid={testid}>{inner}</div>;
}
